import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore'
import { db } from '../firebase'
import swal from 'sweetalert'
import './dash.css'

function MediaConsentResponse() {
  const { token } = useParams()
  const [tokenData, setTokenData] = useState(null)
  const [requestData, setRequestData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadConsent = async () => {
      try {
        const tokenRef = doc(db, 'MediaConsentTokens', token)
        const tokenSnap = await getDoc(tokenRef)
        if (!tokenSnap.exists()) {
          setError('This consent link is invalid or has expired.')
          return
        }

        const tokenInfo = tokenSnap.data()
        setTokenData({ id: tokenSnap.id, ...tokenInfo })

        if (!tokenInfo.requestId) {
          setError('This consent link is missing request information.')
          return
        }

        const requestRef = doc(db, 'MediaConsentRequests', tokenInfo.requestId)
        const requestSnap = await getDoc(requestRef)
        if (!requestSnap.exists()) {
          setError('The media consent request could not be found.')
          return
        }

        setRequestData({ id: requestSnap.id, ...requestSnap.data() })
      } catch (err) {
        console.error('Failed to load consent request', err)
        setError('Unable to load the consent request. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    loadConsent()
  }, [token])

  const respond = async (decision) => {
    if (!tokenData || tokenData.status !== 'pending') return

    setSubmitting(true)
    try {
      const tokenRef = doc(db, 'MediaConsentTokens', token)
      const respondedAt = Timestamp.now()
      await updateDoc(tokenRef, {
        status: decision,
        respondedAt
      })

      setTokenData((prev) => ({
        ...prev,
        status: decision,
        respondedAt
      }))
    } catch (err) {
      console.error('Failed to submit response', err)
      setError('Unable to save your response. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleApprove = async () => {
    if (!tokenData || tokenData.status !== 'pending') return

    if (!tokenData.otpRequired || !tokenData.otpCode) {
      await respond('approved')
      return
    }

    const inputOtp = await swal({
      text: 'Enter the OTP sent to your phone:',
      content: 'input',
      button: {
        text: 'Verify',
        closeModal: false
      }
    })

    if (!inputOtp) return

    setSubmitting(true)
    try {
      const tokenRef = doc(db, 'MediaConsentTokens', token)
      const tokenSnap = await getDoc(tokenRef)
      if (!tokenSnap.exists()) {
        swal('Error', 'Consent token not found.', 'error')
        return
      }

      const tokenInfo = tokenSnap.data()
      if (tokenInfo.status !== 'pending') {
        setTokenData((prev) => ({ ...prev, status: tokenInfo.status }))
        swal('Notice', 'This consent request has already been updated.', 'info')
        return
      }

      if (tokenInfo.otpVerified) {
        swal('Used', 'This OTP has already been used.', 'warning')
        return
      }

      if (tokenInfo.otpCode !== inputOtp) {
        swal('Invalid', 'Wrong OTP. Try again.', 'error')
        return
      }

      const respondedAt = Timestamp.now()
      await updateDoc(tokenRef, {
        status: 'approved',
        respondedAt,
        otpVerified: true,
        otpVerifiedAt: respondedAt
      })

      setTokenData((prev) => ({
        ...prev,
        status: 'approved',
        respondedAt,
        otpVerified: true,
        otpVerifiedAt: respondedAt
      }))
      swal('Success', 'Consent approved.', 'success')
    } catch (err) {
      console.error('Failed to approve consent', err)
      swal('Error', 'Unable to approve consent. Please try again.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const getVideoEmbedUrl = (url) => {
    try {
      const parsed = new URL(url)
      const host = parsed.hostname.replace('www.', '')
      if (host === 'youtu.be') {
        const videoId = parsed.pathname.slice(1)
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
      if (host.includes('youtube.com')) {
        const videoId = parsed.searchParams.get('v')
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
      if (host.includes('vimeo.com')) {
        const parts = parsed.pathname.split('/').filter(Boolean)
        const videoId = parts[parts.length - 1]
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null
      }
    } catch (err) {
      return null
    }
    return null
  }

  const isDirectVideo = (url) => {
    return /\.(mp4|webm|ogg)(\?|#|$)/i.test(url)
  }

  const renderMedia = () => {
    if (!requestData || !requestData.media || requestData.media.length === 0) return null
    return (
      <div className="media-consent-media">
        {requestData.media.map((item) => (
          <div key={item.url} className="media-consent-media-item">
            {item.type && item.type.startsWith('image') && <img src={item.url} alt={item.name || 'Media'} />}
            {item.type && item.type.startsWith('video') && (() => {
              const embedUrl = getVideoEmbedUrl(item.url)
              if (embedUrl) {
                return (
                  <iframe
                    src={embedUrl}
                    title={item.name || 'Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )
              }
              if (isDirectVideo(item.url)) {
                return <video controls src={item.url} />
              }
              return (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                  Open Video
                </a>
              )
            })()}
          </div>
        ))}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="media-consent-public">
        <div className="media-consent-card">Loading consent request...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="media-consent-public">
        <div className="media-consent-card">
          <div className="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    )
  }

  const statusLabel = tokenData?.status || 'pending'
  const respondedAt = tokenData?.respondedAt && tokenData.respondedAt.toDate
    ? tokenData.respondedAt.toDate().toLocaleString()
    : null

  return (
    <div className="media-consent-public">
      <div className="media-consent-card">
        {tokenData?.studentName && (
          <p className="text-muted">Hello {tokenData.studentName},</p>
        )}
        <h3>{requestData?.title || 'Media Consent Request'}</h3>
        <p className="text-muted">{requestData?.description}</p>

        {renderMedia()}

        <div className="media-consent-status">
          <span className={`badge ${statusLabel === 'approved' ? 'bg-success' : statusLabel === 'declined' ? 'bg-danger' : 'bg-warning'}`}>
            {statusLabel.toUpperCase()}
          </span>
          {respondedAt && <span className="text-muted">Responded at: {respondedAt}</span>}
        </div>

        <div className="media-consent-actions">
          <button
            className="btn btn-success"
            type="button"
            disabled={submitting || statusLabel !== 'pending'}
            onClick={handleApprove}
          >
            Approve
          </button>
          <button
            className="btn btn-danger"
            type="button"
            disabled={submitting || statusLabel !== 'pending'}
            onClick={() => respond('declined')}
          >
            Decline
          </button>
        </div>
        <p className="text-muted mt-3">This consent can be withdrawn at any time in writing.</p>
      </div>
    </div>
  )
}

export default MediaConsentResponse
